<template>
  <dialog-base
    :visible.sync="visible"
    title="Đồng bộ thiết bị"
    size="md"
    :use-panel="false"
    :show-confirm="true"
    confirm-text="Đồng bộ"
    cancel-text="Đóng"
    confirm-icon="el-icon-refresh"
    :confirm-loading="confirmingSync || startingSyncJob"
    :confirm-disabled="loadingSchools || loadingDevices || restoringSyncJobs || synchronizationRunning || devices.length < 2"
    :cancel-disabled="confirmingSync || startingSyncJob"
    :close-on-cancel="false"
    :before-close="closeDialog"
    @cancel="closeDialog"
    @confirm="submit"
  >
    <el-alert
      title="Dữ liệu nhận diện đang quản lý trên thiết bị mới sẽ được xóa trước khi sao chép từ thiết bị cũ."
      type="warning"
      show-icon
      :closable="false"
      class="sync-notice"
    />
    <el-form ref="syncForm" :model="form" :rules="(syncing || loadingSchools || loadingDevices || restoringSyncJobs) ? {} : rules" :validate-on-rule-change="false" label-position="top">
        <DeviceSynchronizationProgress
            :job="syncJob" :jobs="syncJobs" :error="trackingError" :loading="restoringSyncJobs"
            @select="selectSynchronizationJob" @refresh="refreshSynchronizationJobs()"
        />
      <el-form-item label="Trường học" prop="idSchool">
        <el-select v-model="form.idSchool" filterable :loading="loadingSchools" :disabled="syncing || loadingSchools"
          placeholder="Chọn trường học" class="sync-control" @change="loadDevices()">
          <el-option v-for="school in schools" :key="school.id" :value="school.id" :label="school.name" />
        </el-select>
      </el-form-item>
      <el-form-item label="Thiết bị cũ (nguồn)" prop="idDeviceOld">
        <el-select v-model="form.idDeviceOld" filterable :loading="loadingDevices" :disabled="syncing || loadingSchools || loadingDevices || !form.idSchool" placeholder="Chọn thiết bị cũ" class="sync-control"
          @change="handleSourceChange">
          <el-option v-for="device in devices" :key="device.id" :value="device.id" :label="deviceLabel(device)" />
        </el-select>
      </el-form-item>
      <el-form-item label="Thiết bị mới (đích)" prop="idDeviceNew">
        <el-select v-model="form.idDeviceNew" filterable :loading="loadingDevices" :disabled="syncing || loadingSchools || loadingDevices || !form.idSchool" placeholder="Chọn thiết bị mới" class="sync-control">
          <el-option v-for="device in devices" :key="device.id" :value="device.id" :label="deviceLabel(device)"
            :disabled="String(device.id) === String(form.idDeviceOld)" />
        </el-select>
      </el-form-item>
      <p v-if="form.idSchool && !loadingSchools && !loadingDevices && devices.length < 2" class="sync-note">
        Trường cần có ít nhất hai thiết bị để thực hiện đồng bộ.
      </p>

    </el-form>
  </dialog-base>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import ManagerRecognitionService from "@/services/ManagerRecognition/ManagerRecognitionService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import DeviceSynchronizationProgress from "@/components/DeviceSynchronizationProgress.vue";
import { createDeviceSynchronizationTracker } from "@/utils/deviceSynchronization";

export default {
  name: "SynchronizeDevicesDialog",
  components: { DialogBase, DeviceSynchronizationProgress },
  mixins: [createDeviceSynchronizationTracker(ManagerRecognitionService, "admin")],
  data() {
    const required = message => [{ required: true, message, trigger: "change" }];
    return {
      visible: false, confirmingSync: false, loadingSchools: false, loadingDevices: false,
      schools: [], devices: [], generation: 0, devicesRequestId: 0,
      form: { idSchool: null, idDeviceOld: null, idDeviceNew: null },
      rules: {
        idSchool: required("Vui lòng chọn trường học"),
        idDeviceOld: required("Vui lòng chọn thiết bị cũ"),
        idDeviceNew: [
          ...required("Vui lòng chọn thiết bị mới"),
          { validator: (rule, value, callback) => callback(value && String(value) === String(this.form.idDeviceOld)
            ? new Error("Thiết bị cũ và thiết bị mới phải khác nhau") : undefined), trigger: "change" },
        ],
      },
    };
  },
  computed: {
    syncing() { return this.confirmingSync || this.startingSyncJob || this.synchronizationRunning; },
  },
  methods: {
    async onSynchronizationRecovered(job) {
      await this.open({ idSchool: job.idSchool, id: job.idDeviceOld }, true);
      if (this.syncJob && this.syncJob.id === job.id) {
        this.form = { idSchool: job.idSchool, idDeviceOld: job.idDeviceOld, idDeviceNew: job.idDeviceNew };
        this.$nextTick(() => this.$refs.syncForm?.clearValidate());
      }
    },
    onSynchronizationAlreadyRunning() {
      NotifyMessage.leftCornerNotificationWarning("Thiết bị đích đang có một tác vụ đồng bộ. Đang hiển thị tiến trình hiện có, không tạo thêm lượt mới.");
    },
    onSynchronizationFinished(job) {
      const message = job.message || "Đã kết thúc lượt đồng bộ";
      if (job.status === "SUCCEEDED") NotifyMessage.leftCornerNotificationSuccess(message);
      else if (job.status === "COMPLETED_WITH_WARNINGS" || job.status === "PARTIAL") NotifyMessage.leftCornerNotificationWarning(message);
      else NotifyMessage.leftCornerNotificationError(message);
      this.$emit("synchronized");
    },

    async open(row = {}, recovered = false) {
      if (this.syncing && !recovered) { this.visible = true; return; }
      const generation = ++this.generation;
      this.devicesRequestId++;
      this.form = { idSchool: row.idSchool || null, idDeviceOld: null, idDeviceNew: null };
      this.schools = []; this.devices = []; this.loadingDevices = false;
      this.visible = true; this.loadingSchools = true;
      this.$nextTick(() => this.$refs.syncForm?.clearValidate());
      try {
        const res = await ManagerRecognitionService.getSchools();
        if (generation !== this.generation) return;
        this.schools = res.data.data || [];
        if (this.form.idSchool) await this.loadDevices(row.id);
      } catch (error) {
        if (generation === this.generation) NotifyMessage.leftCornerNotificationError(
          error?.response?.data?.message || "Không thể tải danh sách trường có thiết bị!");
      } finally {
        if (generation === this.generation) {
          this.loadingSchools = false;
          this.$nextTick(() => { if (generation === this.generation) this.$refs.syncForm?.clearValidate(); });
        }
      }
      if (!recovered && generation === this.generation) await this.refreshSynchronizationJobs(true);
    },
    async loadDevices(preferredSource) {
      const requestId = ++this.devicesRequestId;
      const generation = this.generation;
      const idSchool = this.form.idSchool;
      this.form.idDeviceOld = null; this.form.idDeviceNew = null; this.devices = [];
      this.loadingDevices = false;
      this.$refs.syncForm?.clearValidate();
      if (!idSchool) return;
      this.loadingDevices = true;
      try {
        const res = await ManagerRecognitionService.getDevicesBySchool(idSchool);
        if (requestId !== this.devicesRequestId || generation !== this.generation) return;
        this.devices = res.data.data || [];
        const source = this.devices.find(device => String(device.id) === String(preferredSource));
        if (source) this.form.idDeviceOld = source.id;
      } catch (error) {
        if (requestId === this.devicesRequestId && generation === this.generation) NotifyMessage.leftCornerNotificationError(
          error?.response?.data?.message || "Không thể tải danh sách thiết bị của trường!");
      } finally {
        if (requestId === this.devicesRequestId && generation === this.generation) this.loadingDevices = false;
      }
    },
    handleSourceChange() {
      if (String(this.form.idDeviceOld) === String(this.form.idDeviceNew)) this.form.idDeviceNew = null;
      this.$refs.syncForm?.clearValidate("idDeviceNew");
    },
    deviceLabel(device) { return `${device.id} - ${device.nameDevice || "Thiết bị"}`; },
    closeDialog(done) {
      if (this.confirmingSync || this.startingSyncJob) return;
      this.dismissSynchronizationResult();
      this.visible = false; this.generation++; this.devicesRequestId++;
      if (typeof done === "function") done();
    },
    async submit() {
      if (this.syncing || this.restoringSyncJobs || this.loadingSchools || this.loadingDevices) return;
      const generation = this.generation;
      try { await this.$refs.syncForm.validate(); } catch (_) { return; }
      if (this.syncing || this.restoringSyncJobs || !this.visible || generation !== this.generation || this.loadingSchools || this.loadingDevices) return;
      const payload = { ...this.form };
      const source = this.devices.find(device => String(device.id) === String(payload.idDeviceOld));
      const target = this.devices.find(device => String(device.id) === String(payload.idDeviceNew));
      if (!payload.idSchool || !source || !target || String(source.id) === String(target.id)) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn hai thiết bị khác nhau của cùng một trường!");
        return;
      }
      this.confirmingSync = true;
      try {
        try {
          await this.$confirm(`Xóa dữ liệu nhận diện đang quản lý trên thiết bị mới "${this.deviceLabel(target)}" và đồng bộ từ "${this.deviceLabel(source)}"?`,
            "Xác nhận đồng bộ thiết bị", { type: "warning", confirmButtonText: "Đồng bộ", cancelButtonText: "Hủy", closeOnClickModal: false });
        } catch (_) { return; }
        await this.startSynchronizationJob(payload);
      } catch (error) {
        if (error && error.synchronizationStartUncertain) NotifyMessage.leftCornerNotificationWarning(error.message);
        else NotifyMessage.leftCornerNotificationError(error?.response?.data?.message || error?.message || "Không thể bắt đầu đồng bộ!");
      } finally {
        this.confirmingSync = false;
      }
    },
  },
};
</script>

<style scoped>
.sync-notice { margin-bottom: 18px; }
.sync-control { width: 100%; }
.sync-note { color: var(--text-secondary, #606266); margin: 8px 0; }
</style>
